import { useMemo } from "react";
import { useCustom, type HttpError } from "@refinedev/core";

import { useItemTypeMetadata } from "./useItemTypeMetadata";
import { useListValues } from "./useListValues";
import { useRelatedItems } from "./useRelatedItems";
import { useSiteContext } from "../contexts/SiteContext";
import type {
  PropertyMetadata,
  ResponsiveFormLayout,
  FormSectionConfig,
} from "../types";
import { snakeToCamel } from "../utils/fieldMapping";
import { extractFieldNamesFromLayout } from "../utils/formLayoutUtils";

export interface FormFieldOption {
  label: string;
  value: string;
  /** Additional context shown as secondary text in item reference dropdowns */
  description?: string;
}

export interface FormFieldMetadata extends PropertyMetadata {
  /** Options for list/select fields */
  options?: FormFieldOption[];
  /** Mapped field name for API (camelCase) */
  mappedName: string;
  /** Whether the field is disabled per Aras form layout metadata */
  isDisabledByForm?: boolean;
  /** Whether this field is a site selector (uses pre-loaded site options, simple Select) */
  isSiteField?: boolean;
}

interface UseFormMetadataOptions {
  itemTypeName: string;
  mode: "create" | "edit";
  enabled?: boolean;
}

interface UseFormMetadataResult {
  fields: FormFieldMetadata[];
  sections: FormSectionConfig[];
  /** Form layout from Aras (if available) */
  formLayout: ResponsiveFormLayout | null;
  /** Whether the ItemType has a renderable form layout (responsive or default) */
  hasResponsiveForm: boolean;
  /** Form type: "responsive", "default", or "none" */
  formType: "responsive" | "default" | "none";
  /** Default site ID for create mode (from current site selection) */
  defaultSiteId: string | null;
  isLoading: boolean;
  isError: boolean;
  error: HttpError | null;
  refetch: () => void;
}

/**
 * Hook to get form metadata for dynamic form rendering.
 * Combines ItemType metadata with list values and field configuration.
 * If the ItemType uses a Responsive Form in Aras, loads the layout from Aras.
 */
export const useFormMetadata = ({
  itemTypeName,
  mode,
  enabled = true,
}: UseFormMetadataOptions): UseFormMetadataResult => {
  // Get site context for site_id field options
  const { availableSites, selectedSiteId, userSite, isEnabled: isSiteScopingEnabled } = useSiteContext();

  // Get ItemType metadata (properties, relationships) and grid columns
  const {
    metadata,
    gridColumns,
    isLoading: metadataLoading,
    isError: metadataError,
    error: metadataErr,
    refetch,
  } = useItemTypeMetadata({
    itemTypeName,
    enabled,
  });

  // Fetch Responsive Form layout from Aras
  const {
    data: formLayoutData,
    isLoading: layoutLoading,
    isError: layoutError,
    error: layoutErr,
  } = useCustom<ResponsiveFormLayout>({
    url: `/meta/itemtypes/${itemTypeName}/form-layout`,
    method: "get",
    queryOptions: {
      enabled,
      staleTime: 1000 * 60 * 10, // Cache for 10 minutes
      retry: false, // Don't retry on failure - fallback to static config
    },
  });

  const formLayout = formLayoutData?.data ?? null;
  const hasResponsiveForm = formLayout?.hasResponsiveForm ?? false;
  const formType = formLayout?.formType ?? "none";

  // Mark layoutError as intentionally unused (we fallback gracefully)
  void layoutError;

  // Build a map of dataSource from grid columns (they often have correct values when metadata doesn't)
  const gridColumnDataSourceMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const col of gridColumns) {
      if (col.dataSource) {
        map[col.name] = col.dataSource;
      }
    }
    return map;
  }, [gridColumns]);

  // Data types that use list values from dataSource
  const listDataTypes = new Set([
    "list",
    "color list",
    "color_list",
    "mv_list",
    "multi value list",
    "multi_value_list",
    "filter list",
    "filter_list",
  ]);

  /**
   * Convert snake_case property name to Title Case for list name inference.
   * e.g., "address_type" -> "Address Type"
   */
  const propertyNameToListName = (propertyName: string): string => {
    return propertyName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  /**
   * Gets the list name for a property with fallback chain:
   * 1. Property's dataSource (from metadata)
   * 2. Grid column's dataSource (often has correct value when metadata doesn't)
   * 3. Inferred from property name (e.g., "address_type" -> "Address Type")
   */
  const getListNameForProperty = (
    propertyName: string,
    propDataSource?: string,
  ): string => {
    return (
      propDataSource ||
      gridColumnDataSourceMap[propertyName] ||
      propertyNameToListName(propertyName)
    );
  };

  // Extract list names from properties with list-based data types
  // Uses fallback chain: property dataSource -> grid column dataSource -> inferred name
  const listNames = useMemo(() => {
    if (!metadata?.properties) return [];

    return metadata.properties
      .filter((p) => {
        const normalizedType = p.dataType.toLowerCase().replace(/\s+/g, "_");
        return (
          listDataTypes.has(p.dataType.toLowerCase()) ||
          listDataTypes.has(normalizedType)
        );
      })
      .map((p) => getListNameForProperty(p.name, p.dataSource || undefined))
      .filter((name, index, self) => name && self.indexOf(name) === index); // unique and non-empty
  }, [metadata?.properties, gridColumnDataSourceMap]);

  // Build a mapping from property name to list name (for options lookup)
  const propertyToListNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (!metadata?.properties) return map;

    metadata.properties
      .filter((p) => {
        const normalizedType = p.dataType.toLowerCase().replace(/\s+/g, "_");
        return (
          listDataTypes.has(p.dataType.toLowerCase()) ||
          listDataTypes.has(normalizedType)
        );
      })
      .forEach((p) => {
        map[p.name] = getListNameForProperty(p.name, p.dataSource || undefined);
      });

    return map;
  }, [metadata?.properties, gridColumnDataSourceMap]);

  // Fetch list values for all list fields
  const {
    lists,
    isLoading: listsLoading,
    isError: listsError,
    error: listsErr,
  } = useListValues({
    listNames,
    enabled: enabled && listNames.length > 0,
  });

  // Extract item type references from properties with 'item' dataType
  const relatedItemConfigs = useMemo(() => {
    if (!metadata?.properties) return [];

    return metadata.properties
      .filter((p) => {
        const normalizedType = p.dataType.toLowerCase().replace(/\s+/g, "_");
        return normalizedType === "item" && p.dataSource;
      })
      .map((p) => ({
        itemType: p.dataSource as string,
      }))
      .filter(
        (config, index, self) =>
          self.findIndex((c) => c.itemType === config.itemType) === index,
      ); // unique by itemType
  }, [metadata?.properties]);

  // Fetch related items for item fields
  const {
    items: relatedItems,
    isLoading: relatedItemsLoading,
    isError: relatedItemsError,
    error: relatedItemsErr,
  } = useRelatedItems({
    relatedItems: relatedItemConfigs,
    enabled: enabled && relatedItemConfigs.length > 0,
  });

  /**
   * Map Aras property name to API field name.
   * Automatically converts snake_case to camelCase.
   */
  const mapFieldName = (arasPropertyName: string): string => {
    return snakeToCamel(arasPropertyName);
  };

  // System fields to always exclude from forms
  const systemFields = new Set([
    "id",
    "created_by_id",
    "modified_by_id",
    "owned_by_id",
    "config_id",
    "generation",
    "is_current",
    "major_rev",
    "minor_rev",
    "keyed_name",
    "classification",
    "permission_id",
    "team_id",
    "locked_by_id",
    "current_state",
    "state",
  ]);

  // Extract visible field names from form layout.
  // A field is visible if it's in the layout tree AND not marked hidden in form field metadata.
  // This matches Aras behavior: hidden fields exist in the form definition but are not rendered.
  const formLayoutFieldNames = useMemo((): Set<string> => {
    if (!formLayout) return new Set();

    const allLayoutFields = extractFieldNamesFromLayout(formLayout);

    // Remove fields marked as hidden in form field metadata
    if (formLayout.fields) {
      for (const fieldName of allLayoutFields) {
        if (formLayout.fields[fieldName]?.hidden) {
          allLayoutFields.delete(fieldName);
        }
      }
    }

    return allLayoutFields;
  }, [formLayout]);

  // Process fields: filter, map names, add options
  // All field configuration comes from Aras metadata - no static fallback
  const fields = useMemo((): FormFieldMetadata[] => {
    if (!metadata?.properties) {
      // No metadata available - return empty array
      // The form will show "Form chưa được cấu hình" notification
      return [];
    }

    // Process properties from Aras metadata
    const processedFields: FormFieldMetadata[] = metadata.properties
      .filter((prop) => {
        // Filter out system fields
        if (systemFields.has(prop.name)) return false;

        // Filter out read-only fields in create mode
        if (mode === "create" && prop.isReadOnly) return false;

        // When form layout exists, show fields that are explicitly in the layout.
        // For responsive forms: layout takes precedence over property-level isHidden,
        // because Aras allows placing "hidden" properties on Responsive Forms intentionally
        // (e.g., Person has full_name/phone/email hidden at property level but on the form).
        // For default (traditional) forms: also respect property-level isHidden,
        // because Aras client hides fields with is_hidden=1 even if they exist on the form.
        if (formLayoutFieldNames.size > 0) {
          if (!formLayoutFieldNames.has(prop.name)) return false;
          if (formType === "default" && prop.isHidden) return false;
          return true;
        }

        // No form layout — filter out hidden fields
        if (prop.isHidden) return false;

        return true;
      })
      .map((prop): FormFieldMetadata => {
        // Map field name (Aras snake_case -> API camelCase)
        const mappedName = snakeToCamel(prop.name);

        // Get options for all list-based field types
        let options: FormFieldOption[] | undefined;
        const normalizedType = prop.dataType.toLowerCase().replace(/\s+/g, "_");
        const isListBased =
          listDataTypes.has(prop.dataType.toLowerCase()) ||
          listDataTypes.has(normalizedType);
        const isItemRef = normalizedType === "item";

        if (isListBased) {
          // Use dataSource if available, otherwise use inferred list name from property name
          const listName = prop.dataSource || propertyToListNameMap[prop.name];
          if (listName) {
            const listValues = lists[listName] || [];
            options = listValues.map((lv) => ({
              label: lv.label || lv.value,
              value: lv.value,
            }));
          }
        } else if (isItemRef && prop.dataSource) {
          // For site_id field, use sites from SiteContext instead of fetching all Identity items
          // When site scoping is disabled, leave options empty (Identity is excluded by design)
          if (prop.name === "site_id") {
            if (isSiteScopingEnabled) {
              options = availableSites.map((site) => ({
                label: site.description || site.siteName,
                value: site.siteId,
              }));
            }
          } else {
            // Get options from related items for item reference fields
            options = relatedItems[prop.dataSource] || [];
          }
        }

        // Detect site field: any field whose dataSource is Identity and site scoping is active
        const isSiteField = prop.name === "site_id" && isSiteScopingEnabled && availableSites.length > 0;

        // Check if the form layout marks this field as disabled
        const formFieldMeta = formLayout?.fields?.[prop.name];
        const isDisabledByForm = formFieldMeta?.disabled === true;

        return {
          ...prop,
          mappedName,
          options,
          isDisabledByForm,
          isSiteField,
        };
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);

    return processedFields;
  }, [
    metadata?.properties,
    mode,
    lists,
    relatedItems,
    propertyToListNameMap,
    formLayoutFieldNames,
    formLayout?.fields,
    isSiteScopingEnabled,
    availableSites,
  ]);

  // Get sections config - ONLY from Aras Responsive Form
  // Static config fallback is intentionally removed - all dynamic ItemTypes
  // must have a Responsive Form configured in Aras
  const sections = useMemo((): FormSectionConfig[] => {
    // If we have a Responsive Form layout from Aras, convert it to sections
    if (hasResponsiveForm && formLayout?.order && formLayout.layout) {
      const responsiveSections: FormSectionConfig[] = [];
      const { order, layout } = formLayout;

      // Buffer for collecting consecutive direct fields
      let directFieldsBuffer: string[] = [];
      let directFieldsStartIndex = 0;

      // Helper to flush buffered direct fields as a single section
      const flushDirectFields = () => {
        if (directFieldsBuffer.length === 0) return;

        const fieldNames = directFieldsBuffer.map(mapFieldName);
        responsiveSections.push({
          key: `responsive-fields-${directFieldsStartIndex}`,
          titleKey: "",
          title: undefined,
          fields: fieldNames,
          // Render as single-column rows for clean layout
          rows: fieldNames.map((f) => [f]),
          columns: 1,
          // bordered: undefined means no Card wrapper (line 176 in DynamicFormSection)
          bordered: undefined,
        });
        directFieldsBuffer = [];
      };

      // Each root element in order can be:
      // 1. A columnSet/column defined in layout
      // 2. A direct field name (not in layout) - collected and rendered together
      for (let index = 0; index < order.length; index++) {
        const rootId = order[index];
        const rootElement = layout[rootId];

        // If rootId is not in layout, it's a direct field name
        // Collect consecutive direct fields to render them together
        if (!rootElement) {
          if (directFieldsBuffer.length === 0) {
            directFieldsStartIndex = index;
          }
          directFieldsBuffer.push(rootId);
          continue;
        }

        // We hit a layout element - flush any pending direct fields first
        flushDirectFields();

        // Get fields per column (preserving column structure from Aras)
        const columnFields: string[][] = [];
        const allFieldNames: string[] = [];

        // Helper to recursively get fields from a column
        const getFieldsFromColumn = (elementId: string): string[] => {
          const element = layout[elementId];
          if (!element) {
            // If not in layout, it's a field name
            return [mapFieldName(elementId)];
          }
          // Recursively get fields from children
          const fields: string[] = [];
          for (const childId of element.children) {
            fields.push(...getFieldsFromColumn(childId));
          }
          return fields;
        };

        // Helper to convert column-based layout to row-based layout
        // Aras displays columns side-by-side, pairing fields by index
        // e.g., Column1: [A,B,C], Column2: [X,Y] -> Rows: [[A,X], [B,Y], [C,""]]
        // Empty strings ("") represent empty cells that should still take up space
        const columnsToRows = (columns: string[][]): string[][] => {
          const rows: string[][] = [];
          const numColumns = columns.length;
          const maxLength = Math.max(...columns.map((c) => c.length), 0);

          for (let i = 0; i < maxLength; i++) {
            const row: string[] = [];
            for (let colIdx = 0; colIdx < numColumns; colIdx++) {
              const col = columns[colIdx];
              // Use empty string for missing positions to preserve column layout
              row.push(i < col.length ? col[i] : "");
            }
            // Only add row if it has at least one non-empty field
            if (row.some((f) => f !== "")) {
              rows.push(row);
            }
          }
          return rows;
        };

        // Helper to process all fields from a column that contains mixed children
        // (nested columnsets + direct fields). This handles Aras layouts like:
        // GroupBox -> Column 0 -> [ColumnSet1, ColumnSet2, field, ColumnSet3, field]
        const processColumnWithMixedChildren = (
          columnElement: (typeof layout)[string],
        ): {
          rows: string[][];
          label?: Record<string, string>;
          bordered?: boolean;
        } => {
          const rows: string[][] = [];
          let foundLabel: Record<string, string> | undefined;
          let foundBordered: boolean | undefined;

          for (const childId of columnElement.children) {
            const child = layout[childId];

            if (!child) {
              // Direct field - add as single-column row
              rows.push([mapFieldName(childId)]);
            } else if (
              child.type === "columnSet" ||
              child.type === "gridColumnSet"
            ) {
              // Nested columnset - extract fields from its columns
              const columnChildren = child.children.filter((id) => {
                const c = layout[id];
                return c && c.type === "column";
              });

              if (columnChildren.length >= 2) {
                // Multi-column columnset - extract fields from each column
                // and pair them by index to create rows
                const columnFieldArrays: string[][] = [];
                for (const colId of columnChildren) {
                  const colElement = layout[colId];
                  if (colElement) {
                    const colFields: string[] = [];
                    for (const fieldId of colElement.children) {
                      colFields.push(...getFieldsFromColumn(fieldId));
                    }
                    columnFieldArrays.push(colFields);
                  }
                }
                // Convert columns to rows (pair fields by index)
                // e.g., 2 cols with [A,B,C] and [X,Y,Z] -> [[A,X], [B,Y], [C,Z]]
                // e.g., 3 cols with [A] [B] [C] -> [[A,B,C]]
                const nestedRows = columnsToRows(columnFieldArrays);
                rows.push(...nestedRows);
              } else if (columnChildren.length === 1) {
                // Single column - add each field as its own row (vertical layout)
                const colElement = layout[columnChildren[0]];
                if (colElement) {
                  for (const fieldId of colElement.children) {
                    const fieldNames = getFieldsFromColumn(fieldId);
                    // Each field becomes its own row for vertical stacking
                    for (const fieldName of fieldNames) {
                      rows.push([fieldName]);
                    }
                  }
                }
              }

              // Capture label/bordered from nested columnset if not already found
              if (!foundLabel && child.label) {
                foundLabel = child.label;
              }
              if (foundBordered === undefined && child.bordered !== undefined) {
                foundBordered = child.bordered;
              }
            } else if (child.type === "column") {
              // Direct column child - extract its fields
              const row: string[] = [];
              for (const fieldId of child.children) {
                row.push(...getFieldsFromColumn(fieldId));
              }
              if (row.length > 0) {
                rows.push(row);
              }
            }
          }

          return { rows, label: foundLabel, bordered: foundBordered };
        };

        // Check for pattern: ColumnSet -> single Column -> [ColumnSet(s) + fields]
        // This is common in Aras where GroupBox wraps content
        if (
          (rootElement.type === "columnSet" ||
            rootElement.type === "gridColumnSet") &&
          rootElement.children.length === 1
        ) {
          const singleChildId = rootElement.children[0];
          const singleChild = layout[singleChildId];

          if (singleChild && singleChild.type === "column") {
            // Check children of this column
            const nestedColumnsets = singleChild.children.filter((id) => {
              const c = layout[id];
              return (
                c && (c.type === "columnSet" || c.type === "gridColumnSet")
              );
            });

            const hasDirectFields = singleChild.children.some(
              (id) => !layout[id],
            );

            // Check if root element has no meaningful label (wrapper pattern)
            const rootLabel = rootElement.label?.vi || rootElement.label?.en;
            const isWrapper = !rootLabel || rootLabel.trim() === "";

            // Case 0: Wrapper columnset with multiple labeled children
            // Structure: Wrapper(no label) -> Column -> [ColumnSet1(label), ColumnSet2(label), ...]
            // Each child columnset should become its own section
            if (isWrapper && nestedColumnsets.length >= 2) {
              // Process each child of the column as a separate section
              for (
                let childIdx = 0;
                childIdx < singleChild.children.length;
                childIdx++
              ) {
                const childId = singleChild.children[childIdx];
                const child = layout[childId];

                if (!child) {
                  // Direct field - add as single field section
                  const fieldName = mapFieldName(childId);
                  responsiveSections.push({
                    key: `responsive-section-${index}-${childIdx}`,
                    titleKey: "",
                    title: undefined,
                    fields: [fieldName],
                    rows: [[fieldName]],
                    columns: 1,
                    bordered: undefined,
                  });
                  continue;
                }

                if (
                  child.type === "columnSet" ||
                  child.type === "gridColumnSet"
                ) {
                  // Process this columnset as a separate section
                  const childLabel =
                    child.label?.vi || child.label?.en || undefined;
                  const childBordered = child.bordered;
                  const childRows: string[][] = [];
                  const childFields: string[] = [];

                  // Check if this columnset has multi-column structure
                  const childColumns = child.children.filter((id) => {
                    const c = layout[id];
                    return c && c.type === "column";
                  });

                  if (childColumns.length >= 2) {
                    // Multi-column layout (like Column Set 0 with 2 columns)
                    const columnFieldArrays: string[][] = [];
                    for (const colId of childColumns) {
                      const colElement = layout[colId];
                      if (colElement) {
                        const colFields: string[] = [];
                        for (const fieldId of colElement.children) {
                          colFields.push(...getFieldsFromColumn(fieldId));
                        }
                        columnFieldArrays.push(colFields);
                      }
                    }
                    const rows = columnsToRows(columnFieldArrays);
                    childRows.push(...rows);
                    for (const row of rows) {
                      childFields.push(...row.filter((f) => f !== ""));
                    }

                    responsiveSections.push({
                      key: `responsive-section-${index}-${childIdx}`,
                      titleKey: "",
                      title: childLabel,
                      fields: childFields,
                      rows: childRows,
                      columns: childColumns.length as 1 | 2 | 3 | 4,
                      bordered: childBordered,
                    });
                  } else if (childColumns.length === 1) {
                    // Single column - check for nested columnsets (sub-sections)
                    const innerColumn = layout[childColumns[0]];
                    if (innerColumn) {
                      const innerNestedColumnsets = innerColumn.children.filter(
                        (id) => {
                          const c = layout[id];
                          return (
                            c &&
                            (c.type === "columnSet" ||
                              c.type === "gridColumnSet")
                          );
                        },
                      );

                      if (innerNestedColumnsets.length >= 2) {
                        // Has nested sub-sections (like "Thông tin cá nhân" with sub-sections)
                        // Process each sub-section
                        for (
                          let subIdx = 0;
                          subIdx < innerColumn.children.length;
                          subIdx++
                        ) {
                          const subId = innerColumn.children[subIdx];
                          const subElement = layout[subId];

                          if (!subElement) {
                            // Direct field in this section
                            const fieldName = mapFieldName(subId);
                            childRows.push([fieldName]);
                            childFields.push(fieldName);
                          } else if (
                            subElement.type === "columnSet" ||
                            subElement.type === "gridColumnSet"
                          ) {
                            // Sub-section with its own label
                            const subLabel =
                              subElement.label?.vi ||
                              subElement.label?.en ||
                              undefined;
                            const subBordered = subElement.bordered;
                            const subRows: string[][] = [];
                            const subFields: string[] = [];

                            // Get fields from sub-section
                            const subColumns = subElement.children.filter(
                              (id) => {
                                const c = layout[id];
                                return c && c.type === "column";
                              },
                            );

                            if (subColumns.length >= 1) {
                              for (const subColId of subColumns) {
                                const subColElement = layout[subColId];
                                if (subColElement) {
                                  for (const fieldId of subColElement.children) {
                                    const fieldNames =
                                      getFieldsFromColumn(fieldId);
                                    for (const fieldName of fieldNames) {
                                      subRows.push([fieldName]);
                                      subFields.push(fieldName);
                                    }
                                  }
                                }
                              }
                            }

                            // Create a section for this sub-section
                            responsiveSections.push({
                              key: `responsive-section-${index}-${childIdx}-${subIdx}`,
                              titleKey: "",
                              title: subLabel,
                              fields: subFields,
                              rows: subRows,
                              columns: 1,
                              bordered: subBordered,
                            });
                          }
                        }

                        // If we had any direct fields, add the parent section first
                        if (childFields.length > 0) {
                          // Insert the parent section before its sub-sections
                          // Find where we started adding sub-sections
                          const insertIdx =
                            responsiveSections.length -
                            innerNestedColumnsets.length;
                          responsiveSections.splice(insertIdx, 0, {
                            key: `responsive-section-${index}-${childIdx}-parent`,
                            titleKey: "",
                            title: childLabel,
                            fields: childFields,
                            rows: childRows,
                            columns: 1,
                            bordered: childBordered,
                          });
                        }
                      } else {
                        // Simple single-column section
                        for (const fieldId of innerColumn.children) {
                          const fieldNames = getFieldsFromColumn(fieldId);
                          for (const fieldName of fieldNames) {
                            childRows.push([fieldName]);
                            childFields.push(fieldName);
                          }
                        }

                        responsiveSections.push({
                          key: `responsive-section-${index}-${childIdx}`,
                          titleKey: "",
                          title: childLabel,
                          fields: childFields,
                          rows: childRows,
                          columns: 1,
                          bordered: childBordered,
                        });
                      }
                    }
                  }
                }
              }
              continue;
            }

            // Case 1: Multiple columnsets or mixed (columnsets + direct fields)
            if (nestedColumnsets.length >= 2 || hasDirectFields) {
              // Use the mixed children processor
              const result = processColumnWithMixedChildren(singleChild);

              // Store rows directly for row-based rendering
              const sectionRows: string[][] = [];
              for (const row of result.rows) {
                sectionRows.push(row);
                allFieldNames.push(...row);
              }

              // Get section label from root element
              const sectionLabel =
                rootElement.label?.vi || rootElement.label?.en || undefined;

              // Get bordered from root element
              const sectionBordered = rootElement.bordered;

              responsiveSections.push({
                key: `responsive-section-${index}`,
                titleKey: "",
                title: sectionLabel,
                fields: allFieldNames,
                rows: sectionRows,
                columns: 2,
                bordered: sectionBordered,
              });
              continue;
            }

            // Case 2: Single columnset with multiple columns (standard 2-column layout)
            // Structure: GroupBox -> Column -> ColumnSet -> [Column1, Column2]
            // OR deeper: GroupBox -> Column -> ColumnSet -> Column -> [multiple nested ColumnSets]
            if (nestedColumnsets.length === 1) {
              const nestedColumnset = layout[nestedColumnsets[0]];
              if (nestedColumnset) {
                // Check if nestedColumnset has a single column with multiple nested columnsets
                // Structure: ColumnSet 1 -> Column 2 -> [ColumnSet 3, ColumnSet 4, ...]
                const nestedColChildren = nestedColumnset.children.filter(
                  (id) => {
                    const c = layout[id];
                    return c && c.type === "column";
                  },
                );

                if (nestedColChildren.length === 1) {
                  const innerColumn = layout[nestedColChildren[0]];
                  if (innerColumn) {
                    // Check if this column has multiple nested columnsets
                    const innerNestedColumnsets = innerColumn.children.filter(
                      (id) => {
                        const c = layout[id];
                        return (
                          c &&
                          (c.type === "columnSet" || c.type === "gridColumnSet")
                        );
                      },
                    );

                    // If inner column has multiple nested columnsets, use processColumnWithMixedChildren
                    if (innerNestedColumnsets.length >= 2) {
                      const result =
                        processColumnWithMixedChildren(innerColumn);

                      const sectionRows: string[][] = [];
                      for (const row of result.rows) {
                        sectionRows.push(row);
                        allFieldNames.push(...row.filter((f) => f !== ""));
                      }

                      // Get section label - prefer nested columnset label if it's a GroupBox
                      const sectionLabel =
                        nestedColumnset.label?.vi ||
                        nestedColumnset.label?.en ||
                        rootElement.label?.vi ||
                        rootElement.label?.en ||
                        undefined;

                      // Get bordered - prefer nested columnset if it has bordered flag
                      const sectionBordered =
                        nestedColumnset.bordered ?? rootElement.bordered;

                      responsiveSections.push({
                        key: `responsive-section-${index}`,
                        titleKey: "",
                        title: sectionLabel,
                        fields: allFieldNames,
                        rows: sectionRows,
                        columns: 2,
                        bordered: sectionBordered,
                      });
                      continue;
                    }
                  }
                }

                // Helper to find the innermost multi-column columnset
                // Handles deep nesting like: ColumnSet -> Column -> ColumnSet -> [Col1, Col2]
                const findMultiColumnStructure = (
                  columnset: (typeof layout)[string],
                ): string[] | null => {
                  const colChildren = columnset.children.filter((id) => {
                    const c = layout[id];
                    return c && c.type === "column";
                  });

                  // If this columnset has 2+ columns, return them
                  if (colChildren.length >= 2) {
                    return colChildren;
                  }

                  // If single column, check if it contains another columnset
                  if (colChildren.length === 1) {
                    const singleCol = layout[colChildren[0]];
                    if (singleCol) {
                      for (const childId of singleCol.children) {
                        const child = layout[childId];
                        if (
                          child &&
                          (child.type === "columnSet" ||
                            child.type === "gridColumnSet")
                        ) {
                          // Recursively check the nested columnset
                          const result = findMultiColumnStructure(child);
                          if (result) return result;
                        }
                      }
                    }
                  }

                  return null;
                };

                const columnChildren =
                  findMultiColumnStructure(nestedColumnset);

                if (columnChildren && columnChildren.length >= 2) {
                  // Extract fields from each column
                  const columnFieldArrays: string[][] = [];
                  for (const colId of columnChildren) {
                    const colElement = layout[colId];
                    if (colElement) {
                      const colFields: string[] = [];
                      for (const fieldId of colElement.children) {
                        colFields.push(...getFieldsFromColumn(fieldId));
                      }
                      columnFieldArrays.push(colFields);
                    }
                  }

                  // Convert columns to rows (pair fields by index)
                  const sectionRows = columnsToRows(columnFieldArrays);
                  for (const row of sectionRows) {
                    allFieldNames.push(...row);
                  }

                  // Get section label - prefer nested columnset label if it's a GroupBox
                  const sectionLabel =
                    nestedColumnset.label?.vi ||
                    nestedColumnset.label?.en ||
                    rootElement.label?.vi ||
                    rootElement.label?.en ||
                    undefined;

                  // Get bordered - prefer nested columnset if it has bordered flag
                  const sectionBordered =
                    nestedColumnset.bordered ?? rootElement.bordered;

                  responsiveSections.push({
                    key: `responsive-section-${index}`,
                    titleKey: "",
                    title: sectionLabel,
                    fields: allFieldNames,
                    rows: sectionRows,
                    columns: columnChildren.length as 1 | 2 | 3 | 4,
                    bordered: sectionBordered,
                  });
                  continue;
                }
              }
            }
          }
        }

        // Fallback to original logic for simpler structures
        {
          let columnsToProcess: string[] = rootElement.children;

          // If there's exactly one child and it's a columnset, use its children
          if (rootElement.children.length === 1) {
            const potentialNested = layout[rootElement.children[0]];
            if (
              potentialNested &&
              (potentialNested.type === "columnSet" ||
                potentialNested.type === "gridColumnSet")
            ) {
              columnsToProcess = potentialNested.children;
            }
          }

          // Process each column in the columnset
          for (const columnId of columnsToProcess) {
            const columnElement = layout[columnId];
            if (columnElement && columnElement.type === "column") {
              const columnFieldNames: string[] = [];
              for (const fieldId of columnElement.children) {
                columnFieldNames.push(...getFieldsFromColumn(fieldId));
              }
              columnFields.push(columnFieldNames);
              allFieldNames.push(...columnFieldNames);
            } else {
              const fields = getFieldsFromColumn(columnId);
              columnFields.push(fields);
              allFieldNames.push(...fields);
            }
          }
        }

        // Get section label from root element
        const sectionLabel =
          rootElement.label?.vi || rootElement.label?.en || undefined;

        // Get bordered from root element
        const sectionBordered = rootElement.bordered;

        // Convert columns to rows for consistent row-based rendering
        // This handles cases like: 2 columns with [A,B,C] and [X,Y] -> [[A,X], [B,Y], [C]]
        if (columnFields.length >= 2) {
          const sectionRows = columnsToRows(columnFields);
          responsiveSections.push({
            key: `responsive-section-${index}`,
            titleKey: "",
            title: sectionLabel,
            fields: allFieldNames,
            rows: sectionRows,
            columns: 2,
            bordered: sectionBordered,
          });
        } else {
          // Single column - render fields as single-column rows
          const sectionRows =
            columnFields.length > 0 ? columnFields[0].map((f) => [f]) : [];
          responsiveSections.push({
            key: `responsive-section-${index}`,
            titleKey: "",
            title: sectionLabel,
            fields: allFieldNames,
            rows: sectionRows,
            columns: 1,
            bordered: sectionBordered,
          });
        }
      }

      // Flush any remaining direct fields at the end
      flushDirectFields();

      return responsiveSections;
    }

    // No Responsive Form configured - return empty sections
    // DynamicForm will show "Form chưa được cấu hình" notification
    return [];
  }, [hasResponsiveForm, formLayout]);

  // Compute default site ID for create mode:
  // Use current selected site, or user's assigned site
  // Only if the ItemType actually has a site_id property
  const hasSiteIdField = useMemo(() => {
    return metadata?.properties?.some((p) => p.name === "site_id") ?? false;
  }, [metadata?.properties]);

  const defaultSiteId = useMemo(() => {
    if (!isSiteScopingEnabled || !hasSiteIdField) return null;
    return selectedSiteId || userSite?.siteId || null;
  }, [isSiteScopingEnabled, hasSiteIdField, selectedSiteId, userSite?.siteId]);

  return {
    fields,
    sections,
    formLayout,
    hasResponsiveForm,
    formType,
    defaultSiteId,
    isLoading:
      metadataLoading || listsLoading || relatedItemsLoading || layoutLoading,
    isError: metadataError || listsError || relatedItemsError,
    error: metadataErr || listsErr || relatedItemsErr || layoutErr || null,
    refetch,
  };
};
